import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import './FlowLayout.css';
import './AppointmentAvailability.css';

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
const TIME_BLOCKS = ['8–10 AM', '10–12 PM', '12–4 PM', '5–8 PM'];
const WEEK_OPTIONS = [
  { label: 'ASAP' },
  { label: '2 Weeks' },
  { label: '3 Weeks' },
  { label: 'Flexible' },
];

function AppointmentAvailability() {
  const navigate = useNavigate();
  const [activeWeek, setActiveWeek] = useState(0);
  const [availability, setAvailability] = useState({});

  // Get the selected location from localStorage
  const locationId = JSON.parse(localStorage.getItem('locations') || '[]')[0] || '';
  const locationName = localStorage.getItem('selectedLocationName') || 'Your selected enrollment center';
  const locationAddress = localStorage.getItem('selectedLocationAddress') || '';
  const mapsUrl = locationAddress ? 'https://www.google.com/maps/search/?api=1&query=' + encodeURIComponent(locationAddress) : 'https://www.google.com/maps';

  const toggleSlot = (day, time) => {
    const key = day + '|' + time;
    setAvailability(function(prev) {
      var next = Object.assign({}, prev);
      if (next[key]) {
        delete next[key];
      } else {
        next[key] = true;
      }
      return next;
    });
  };

  const selectedCount = Object.keys(availability).length;

  const handleSubmit = () => {
    if (selectedCount === 0) return;
    localStorage.setItem('selectedSlots', JSON.stringify(availability));
    localStorage.setItem('selectedWeek', JSON.stringify(activeWeek));
    navigate('/connect');
    window.scrollTo(0, 0);
  };

  return (
    <div className="flow-page">
      <Helmet>
        <title>Select Availability</title>
        <meta name="description" content="Select when you're available for your Global Entry enrollment interview." />
      </Helmet>

      {/* Progress Bar */}
      <div className="progress-bar-container" role="progressbar" aria-valuenow={3} aria-valuemin={1} aria-valuemax={5} aria-label="Step 3 of 5: Availability">
        <div className="progress-bar-header">
          <button className="flow-back" onClick={function() { navigate('/autobook'); window.scrollTo(0, 0); }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
            Back
          </button>
          <div className="progress-bar-label">Step 3 of 5: Availability</div>
        </div>
        <div className="progress-bar-track">
          <div className="progress-bar-fill" style={{ width: '60%' }} />
        </div>
      </div>

      {/* Split layout */}
      <div className="flow-layout">
        {/* Left column */}
        <div className="flow-left">
          <h1 className="flow-heading">
            When would you like to schedule your appointment?
          </h1>

          <div className="location-highlight" style={{ marginTop: '48px' }}>
            <p className="location-highlight__label">Your selected location</p>
            <h3 className="location-highlight__name">{locationName}</h3>
            {locationAddress && <p className="info-card__text" style={{ marginTop: '8px', marginBottom: '0' }}>{locationAddress}</p>}
            <a
              className="info-card__link"
              href={mapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{ display: 'inline-block', marginTop: '16px' }}
            >
              View on Google Maps →
            </a>
          </div>
        </div>

        {/* Right column */}
        <div className="flow-right">
          <div className="panel-card">
            {/* Week range selector header */}
            <p className="avail-section-label">How soon do you need your appointment?</p>

            {/* Segmented control */}
            <div className="segmented-control">
              {WEEK_OPTIONS.map(function(opt, i) {
                return (
                  <button
                    key={i}
                    className={'segmented-control__btn' + (activeWeek === i ? ' segmented-control__btn--active' : '')}
                    onClick={function() { setActiveWeek(i); }}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>

            {/* Availability description */}
            <p className="avail-section-label" style={{ marginTop: '8px' }}>
              Tell us when you're generally available over the next {(activeWeek + 1)} {activeWeek === 0 ? 'week' : 'weeks'}
            </p>

            {/* Checkbox grid */}
            <div className="avail-grid-wrapper">
              <div className="avail-grid">
                {/* Header row */}
                <div className="avail-grid__row avail-grid__row--header">
                  <div className="avail-grid__day-cell"></div>
                  {TIME_BLOCKS.map(function(time) {
                    return <div key={time} className="avail-grid__time-header">{time}</div>;
                  })}
                </div>

                {/* Day rows */}
                {DAYS.map(function(day) {
                  return (
                    <div key={day} className="avail-grid__row">
                      <div className="avail-grid__day-cell">{day}</div>
                      {TIME_BLOCKS.map(function(time) {
                        var key = day + '|' + time;
                        var isChecked = !!availability[key];
                        return (
                          <div key={time} className="avail-grid__check-cell">
                            <label className="avail-checkbox" aria-label={day + ' ' + time}>
                              <input
                                type="checkbox"
                                checked={isChecked}
                                onChange={function() { toggleSlot(day, time); }}
                              />
                              <span className={'avail-checkbox__box' + (isChecked ? ' avail-checkbox__box--checked' : '')}>
                                {isChecked && (
                                  <svg viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="20 6 9 17 4 12" />
                                  </svg>
                                )}
                              </span>
                            </label>
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Disclaimer */}
            <p className="avail-disclaimer">If something pops up within 48 hours, we will not book you for that appointment to give you enough planning time.</p>

            {/* Actions */}
            <div className="avail-actions">
              <button className="flow-text-link" onClick={function() { navigate('/calendar-specific'); }}>
                Choose Specific Dates Instead
              </button>
              <button
                className="flow-btn-primary"
                onClick={handleSubmit}
                disabled={selectedCount === 0}
              >
                Submit
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AppointmentAvailability;
